import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addNewAssetAsync,
  AssetItem,
  AssetSearchResponse,
  Applications,
  ControllerPort,
  ControllerType,
  editAssetAsync,
  fetchAssetApplicationsDataAsync,
  fetchAssetPOCTypeByApplicationAsync,
  fetchAssetControllerPortDataAsync,
  fetchAssetControllerTypeDataAsync,
  fetchAssetDataAsync,
  fetchAssetDataByIdAsync,
  searchAssetByNameAsync,
  Timezone,
  fetchAssetTimezoneDataAsync,
  MasterDataType,
  fetchAssetMasterDataTypeAsync,
} from "./assetService.tsx";
import { CustomerItem } from "../customerDetails/customerService.ts";

interface AssetState {
  masterDataLoading: "idle" | "loading" | "succeeded" | "failed";
  asset: AssetItem[];
  customer: CustomerItem[];
  searchResults: AssetSearchResponse[];
  assetToEdit: AssetItem | null;
  applications: Applications[];
  port: ControllerPort[];
  type: ControllerType[];
  timezone: Timezone[];
  businessUnit: MasterDataType[];
  fields: MasterDataType[];
  status: "idle" | "loading" | "succeeded" | "failed";
  assetLoading: boolean;
  assetSearchLoading: boolean;
  assetSaveLoading: boolean;
  assetDeleteLoading: { [key: string]: boolean };
  error: string | null;
}

const initialState: AssetState = {
  asset: [],
  searchResults: [],
  assetToEdit: null,
  customer: [],
  applications: [],
  port: [],
  type: [],
  timezone: [],
  businessUnit: [],
  fields: [],
  status: "idle",
  assetLoading: false,
  assetSearchLoading: false,
  assetSaveLoading: false,
  assetDeleteLoading: {},
  error: null,
  masterDataLoading: "idle"
};

// Async thunk for loading assets
export const loadAsset = createAsyncThunk(
  "asset/fetchAssetDataAsync",
  async () => {
    const response = await fetchAssetDataAsync();
    return response;
  }
);

// Async thunk for fetching a asset by ID
export const fetchAssetById = createAsyncThunk(
  "asset/fetchAssetDataByIdAsync",
  async (id: string) => {
    const response = await fetchAssetDataByIdAsync(id);
    return response;
  }
);

// Async thunk for searching asset by name
export const searchAssetByName = createAsyncThunk(
  "asset/searchAssetByNameAsync",
  async (name: string) => {
    const response = await searchAssetByNameAsync(name);
    return response;
  }
);

// Async thunk for loading applications
export const loadAssetApplications = createAsyncThunk(
  "asset/fetchAssetApplicationsDataAsync",
  async () => {
    const response = await fetchAssetApplicationsDataAsync();
    return response;
  }
);

// Async thunk for fetching poc type by application id
export const loadAssetPOCTypeByApplication = createAsyncThunk(
  "asset/fetchAssetPOCTypeByApplicationAsync",
  async (id: string) => {
    const response = await fetchAssetPOCTypeByApplicationAsync(id);
    return response;
  }
);

// Async thunk for loading controller port
export const loadAssetControllerPort = createAsyncThunk(
  "asset/fetchAssetControllerPortDataAsync",
  async () => {
    const response = await fetchAssetControllerPortDataAsync();
    return response;
  }
);

// Async thunk for loading controller type
export const loadAssetControllerType = createAsyncThunk(
  "asset/fetchAssetControllerTypeDataAsync",
  async () => {
    const response = await fetchAssetControllerTypeDataAsync();
    return response;
  }
);

// Async thunk for loading timezone
export const loadAssetTimezone = createAsyncThunk(
  "asset/fetchAssetTimezoneDataAsync",
  async () => {
    const response = await fetchAssetTimezoneDataAsync();
    return response;
  }
);

// Async thunk for loading business units
export const loadAssetMasterDataType = createAsyncThunk(
  "asset/fetchAssetMasterDataTypeAsync",
  async (masterDataType: string) => {
    const response = await fetchAssetMasterDataTypeAsync(masterDataType);
    return response;
  }
);

export const addAsset = createAsyncThunk(
  "asset/addNewUserAsync",
  async (newAsset: AssetItem, { rejectWithValue }) => {
    try {
      const response = await addNewAssetAsync(newAsset);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Async thunk for editing asset
export const editAsset = createAsyncThunk(
  "asset/editAssetAsync",
  async (
    payload: { assetId: string; updatedAsset: AssetItem },
    { rejectWithValue }
  ) => {
    const { assetId, updatedAsset } = payload;
    try {
      const finalupdatedUser = {
        ...updatedAsset,
      };

      const response = await editAssetAsync(assetId, finalupdatedUser);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const toggleEnableAsset = createAsyncThunk(
  "asset/deleteAssetAsync",
  async (
    { assetId, isEnabled }: { assetId: string; isEnabled: boolean },
    { rejectWithValue }
  ) => {
    try {
      const asset = await fetchAssetDataByIdAsync(assetId);
      const updatedAsset = {
        ...asset,
        isEnabled: !isEnabled,
        cnX_WellId: asset?.cnX_WellId ?? null,
        lastModifiedDate: asset?.lastModifiedDate
          ? asset.lastModifiedDate
          : new Date().toISOString(),
        poctype: {
          ...asset?.pocType,
          description: asset?.pocType?.description ?? "",
        },
        action: isEnabled ? "disable" : "enable",
      };
      const response = await editAssetAsync(assetId, updatedAsset);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

const AssetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    resetAssetToEdit(state) {
      state.assetToEdit = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAsset.pending, (state) => {
        state.assetLoading = true;
      })
      .addCase(loadAsset.fulfilled, (state, action) => {
        state.assetLoading = false;
        state.asset = action.payload;
      })
      .addCase(loadAsset.rejected, (state, action) => {
        state.assetLoading = false;
        state.error = action.error.message || "Failed to load Asset";
      })
      .addCase(fetchAssetById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAssetById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assetToEdit = action.payload;
      })
      .addCase(fetchAssetById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to fetch asset by ID";
      })
      .addCase(searchAssetByName.pending, (state) => {
        state.assetSearchLoading = true;
      })
      .addCase(searchAssetByName.fulfilled, (state, action) => {
        state.assetSearchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchAssetByName.rejected, (state, action) => {
        state.assetSearchLoading = false;
        state.error = action.error.message || "Failed to search Customer";
      })
      .addCase(addAsset.pending, (state) => {
        state.status = "loading";
        state.assetSaveLoading = true;
      })
      .addCase(addAsset.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assetSaveLoading = false;
        state.asset.push(action.payload as AssetItem);
      })
      .addCase(addAsset.rejected, (state, action) => {
        state.status = "failed";
        state.assetSaveLoading = false;
        state.error = action.error.message || "Failed to add asset";
      })
      .addCase(editAsset.pending, (state) => {
        state.status = "loading";
        state.assetSaveLoading = true;
      })
      .addCase(editAsset.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.assetSaveLoading = false;
        if (action.payload) {
          const updatedAsset = action.payload;
          const index = state.asset.findIndex(
            (user) => user.id === updatedAsset.id
          );
          if (index !== -1) {
            state.asset[index] = updatedAsset;
          } else {
            state.asset.push(updatedAsset);
          }
        }
      })
      .addCase(editAsset.rejected, (state, action) => {
        state.status = "failed";
        state.assetSaveLoading = false;
        state.error = action.error.message || "Failed to edit asset";
      })
      .addCase(toggleEnableAsset.pending, (state, action) => {
        const assetId = action.meta.arg.assetId;
        state.assetDeleteLoading = {
          ...state.assetDeleteLoading,
          [assetId]: true,
        };
      })
      .addCase(toggleEnableAsset.fulfilled, (state, action) => {
        const updatedAsset = action.payload;
        if (!Array.isArray(updatedAsset)) {
          console.error("Unexpected response format:", updatedAsset);
          return;
        }

        updatedAsset.forEach((updatedAsset) => {
          const assetId = updatedAsset?.id;
          if (!assetId) {
            console.error("AssetId is undefined");
            return;
          }

          state.assetDeleteLoading = {
            ...state.assetDeleteLoading,
            [assetId]: false,
          };
          const index = state.asset.findIndex((asset) => asset.id === assetId);
          if (index !== -1) {
            state.asset[index] = { ...state.asset[index], ...updatedAsset };
          }
        });
        state.asset = [...state.asset];
      })

      .addCase(toggleEnableAsset.rejected, (state, action) => {
        const assetId = action.meta.arg.assetId;
        state.assetDeleteLoading = {
          ...state.assetDeleteLoading,
          [assetId]: false,
        };
      })
      .addCase(loadAssetApplications.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadAssetApplications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.applications = action.payload;
      })
      .addCase(loadAssetApplications.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.error.message || "Failed to load asset applications";
      })
      .addCase(loadAssetPOCTypeByApplication.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadAssetPOCTypeByApplication.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.type = action.payload;
      })
      .addCase(loadAssetPOCTypeByApplication.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.error.message ||
          "Failed to load Controller Types by application id";
      })
      .addCase(loadAssetControllerPort.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadAssetControllerPort.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.port = action.payload;
      })
      .addCase(loadAssetControllerPort.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to load Controller Ports";
      })
      .addCase(loadAssetControllerType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadAssetControllerType.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.type = action.payload;
      })
      .addCase(loadAssetControllerType.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to load Controller Types";
      })
      .addCase(loadAssetTimezone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadAssetTimezone.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.timezone = action.payload;
      })
      .addCase(loadAssetTimezone.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to load timezone";
      })
      .addCase(loadAssetMasterDataType.pending, (state) => {
        state.masterDataLoading = "loading";
      })
      .addCase(loadAssetMasterDataType.fulfilled, (state, action) => {
        state.masterDataLoading = "succeeded";
        if (action.meta.arg === 'Field')
          state.fields = action.payload;
        if (action.meta.arg === 'BusinessUnit')
          state.businessUnit = action.payload;
      })
      .addCase(loadAssetMasterDataType.rejected, (state, action) => {
        state.masterDataLoading = "failed";
        state.error = action.error.message || "Failed to load Master Data Types";
      });
  },
});

export const { resetAssetToEdit } = AssetSlice.actions;

export default AssetSlice.reducer;
